import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  makeStyles,
  CircularProgress,
  Button,
  Divider,
  IconButton,
  Dialog,
} from "@material-ui/core";
import ArrowBack from "@material-ui/icons/ArrowBack";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import axios from "axios";
import { appUrl } from "../../../../../../utils/config";
import ProductDetails from "../Stepper/steps/ProductDetails";
import StatusSelection from "../Stepper/steps/StatusSelection";
import AllocationConfigurations from "../Stepper/steps/AllocationConfigurations";
import OtherConfigurations from "../Stepper/steps/OtherConfigurations";
import ProcessFlowConfiguration from "../Stepper/steps/ProcessFlowConfiguration";
import ShipmentConfigurations from "../Stepper/steps/ShipmentConfigurations";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import isEqual from 'lodash.isequal';
import StyledDialog from "../../../../../common/models/Dialog";
import { DocumentDeliverables } from "../Stepper/steps";


const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    width: "100%",
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    color: "#ec6535",
    marginBottom: "1rem",
    marginTop: "1rem",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  iconButtonStyle: {
    color: "black",
    border: "1px solid black",
    borderRadius: "5px",
    padding: "4px",
  },
  sectionPaper: {
    padding: 0,
    marginBottom: 0,
  },
  reviewSection: {
    marginBottom: 50,
    position: "relative",
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 0,
    marginBottom: 0,
  },
  editButton: {
    backgroundColor: "#ec6535",
    color: "white",
    "&:hover": {
      backgroundColor: "#d45328",
    },
  },
  saveButton: {
    borderRadius: "5rem",
    marginLeft: "1rem",
  },
  discardButton: {
    borderRadius: "5rem",


  },
  buttonProgress: {
    color: "white",
    marginLeft: theme.spacing(1),
  },
  updateChangesButton: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 40,
    // padding: "0 30px",
    // height: 44,
    // fontWeight: 500,
    // fontSize: 16,
    minWidth: 180,
    boxShadow: "none",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 32,
    '&:hover': {
      backgroundColor: "#d45328",
      boxShadow: "none",
    },
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  noProductDataText: {
    marginTop: 50,
    marginBottom: 50,
    textAlign: 'center',
  },
}));

// Normalization helpers
function normalizeFgStatuses(fgStatuses) {
  if (!fgStatuses) return {};
  const normalizeArray = arr =>
    (arr || [])
      .map(s => ({
        status_id: s.status_id,
        status_name: s.status_name,
        activated: s.activated ?? !!s.value // Accept either field
      }))
      .sort((a, b) => String(a.status_id).localeCompare(String(b.status_id)));

  const normalized = {
    poStatuses: normalizeArray(fgStatuses.poStatuses),
  };

  // Only return statuses that are activated
  return {
    poStatuses: normalized.poStatuses.filter(s => s.activated),
  };
}

function normalizeDocumentDeliverables(docs) {
  if (!Array.isArray(docs)) return [];
  return docs
    .map(doc => ({
      documentTypeID: doc.documentTypeID || "",
      fileFormatTypeID: doc.fileFormatTypeID || "",
      orgResponsibleID: doc.orgResponsibleID || doc.uploadOrgCollaboratorTypeID || "",
      mandatory: !!(doc.mandatory ?? doc.documentMandatoryFlag),
      startStatus: doc.startStatus || doc.startOfUploadStatus || "Not Specified",
      endStatus: doc.endStatus || doc.endOfUploadStatus || "Not Specified",
    }))
    .sort((a, b) => String(a.documentTypeID).localeCompare(String(b.documentTypeID)));
}

// Add normalization for shipment configuration
function normalizeShipmentConfig(config) {
  return {
    dispatchDuration:
      config?.dispatchDuration === "" || config?.dispatchDuration == null
        ? null
        : Number(config.dispatchDuration)
  };
}

// Add normalization for process flow configuration
function normalizeProcessFlow(config) {
  return {
    sponsorCQAApprovalReq: config?.sponsorCQAApprovalReq ?? false,
    stockCheckReq: config?.stockCheckReq ?? false,
  };
}

// Add normalization for product details
function normalizeProductDetails(current, original) {
  const currentKeys = Object.keys(current || {});
  const originalKeys = Object.keys(original || {});
  const commonKeys = currentKeys.filter(key => originalKeys.includes(key));
  const normCurrent = {};
  const normOriginal = {};
  
  for (const key of commonKeys) {
    // Special handling for receivingStorageLocationIDsFromSponsorERP field
    if (key === 'receivingStorageLocationIDsFromSponsorERP') {
      // Normalize both to comma-separated string format for comparison
      const normalizeStorageIds = (value) => {
        if (Array.isArray(value)) {
          return value.join(',');
        }
        if (typeof value === 'string') {
          return value;
        }
        return '';
      };
      
      normCurrent[key] = normalizeStorageIds(current[key]);
      normOriginal[key] = normalizeStorageIds(original[key]);
      
    } else if (key === 'cmoProductDetailsList') {
      // Special handling for cmoProductDetailsList array - deep comparison
      normCurrent[key] = Array.isArray(current[key]) ? JSON.stringify(current[key]) : JSON.stringify([]);
      normOriginal[key] = Array.isArray(original[key]) ? JSON.stringify(original[key]) : JSON.stringify([]);
    } else {
      normCurrent[key] = current[key];
      normOriginal[key] = original[key];
    }
  }
  

  
  return [normCurrent, normOriginal];
}

// Add denormalization for allocation configuration
function denormalizeAllocationConfig(data, poStatuses) {
  const result = {
    showExpiredLots: data?.showExpiredLots === "YES",
    defaultAllocationReq: data?.defaultAllocationReq === "YES",
    qtyVarianceAcceptanceLimit: Number(data?.qtyVarianceAcceptanceLimit),
    allowMultipleLotAllocation: data?.allowMultipleLotAllocation === "YES",
    allowOverAllocation: data?.allowOverAllocation === "NA" ? undefined : data?.allowOverAllocation === "YES", 
    overAllocationLimit: data?.overAllocationLimit === "NA" ? undefined : Number(data?.overAllocationLimit),
    allowUnderAllocation: data?.allowUnderAllocation === "NA" ? undefined : data?.allowUnderAllocation === "YES",
    allowUnderAllocationLimit: data?.underAllocationLimit === "NA" ? undefined : Number(data?.underAllocationLimit),
    holdTimeBtnCreatedAndSWCMO: data?.holdTimeBtnCreatedAndSWCMO === "NA" ? undefined : Number(data?.holdTimeBtnCreatedAndSWCMO),
    allowLotAllocationBy: data?.allowLotAllocationBy === "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" ? "all" : data?.allowLotAllocationBy,
    sponsorTriggerStart: data?.sponsorTriggerStart === "NA" ? undefined : data?.sponsorTriggerStart === "PO Status" || data?.sponsorTriggerStart === "poStatus" ? "poStatus" : data?.sponsorTriggerStart === "No. of days before scheduled manufacturing start date" || data?.sponsorTriggerStart === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    sponsorTriggerEnd: data?.sponsorTriggerEnd === "NA" ? undefined : data?.sponsorTriggerEnd === "PO Status" || data?.sponsorTriggerEnd === "poStatus" ? "poStatus" : data?.sponsorTriggerEnd === "No. of days before scheduled manufacturing start date" || data?.sponsorTriggerEnd === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    cmoTriggerStart: data?.cmoTriggerStart === "NA" ? undefined : data?.cmoTriggerStart === "PO Status" || data?.cmoTriggerStart === "poStatus" ? "poStatus" : data?.cmoTriggerStart === "No. of days before scheduled manufacturing start date" || data?.cmoTriggerStart === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    cmoTriggerEnd: data?.cmoTriggerEnd === "NA" ? undefined : data?.cmoTriggerEnd === "PO Status" || data?.cmoTriggerEnd === "poStatus" ? "poStatus" : data?.cmoTriggerEnd === "No. of days before scheduled manufacturing start date" || data?.cmoTriggerEnd === "scheduleMfgStartDateBefore" ? "scheduleMfgStartDateBefore" : "expectedDeliveryDateBefore",
    sponsorTriggerStartValue: data?.sponsorTriggerStartValue === "NA" ? undefined : (data?.sponsorTriggerStart === "poStatus" || data?.sponsorTriggerStart === "PO Status") ? (poStatuses?.find((status) => status.status_name === data.sponsorTriggerStartValue)?.status_id ?? undefined) : Number(data.sponsorTriggerStartValue),
    sponsorTriggerEndValue: data?.sponsorTriggerEndValue === "NA" ? undefined : (data?.sponsorTriggerEnd === "poStatus" || data?.sponsorTriggerEnd === "PO Status") ? (poStatuses?.find((status) => status.status_name === data.sponsorTriggerEndValue)?.status_id ?? undefined) : Number(data.sponsorTriggerEndValue),
    cmoTriggerStartValue: data?.cmoTriggerStartValue === "NA" ? undefined : (data?.cmoTriggerStart === "poStatus" || data?.cmoTriggerStart === "PO Status") ? (poStatuses?.find((status) => status.status_name === data.cmoTriggerStartValue)?.status_id ?? undefined) : Number(data.cmoTriggerStartValue),
    cmoTriggerEndValue: data?.cmoTriggerEndValue === "NA" ? undefined : (data?.cmoTriggerEnd === "poStatus" || data?.cmoTriggerEnd === "PO Status") ? (poStatuses?.find((status) => status.status_name === data.cmoTriggerEndValue)?.status_id ?? undefined) : Number(data.cmoTriggerEndValue),
  };
  
  return result;
}

// Add normalization for allocation configuration
function normalizeAllocationConfig(backendData, poStatuses) {
  const result = {
    showExpiredLots: (backendData?.showExpiredLots === true || backendData?.showExpiredLots === "YES") ? "YES" : 
                     (backendData?.showExpiredLots === false || backendData?.showExpiredLots === "NO") ? "NO" : "NO",
    defaultAllocationReq: (backendData?.defaultAllocationReq === true || backendData?.defaultAllocationReq === "YES") ? "YES" : 
                          (backendData?.defaultAllocationReq === false || backendData?.defaultAllocationReq === "NO") ? "NO" : "NO",
    qtyVarianceAcceptanceLimit: backendData?.qtyVarianceAcceptanceLimit?.toString() || "10",
    allowMultipleLotAllocation: (backendData?.allowMultipleLotAllocation === true || backendData?.allowMultipleLotAllocation === "YES") ? "YES" : 
                                (backendData?.allowMultipleLotAllocation === false || backendData?.allowMultipleLotAllocation === "NO") ? "NO" : "NO",
    allowOverAllocation: (backendData?.allowOverAllocation === undefined || backendData?.allowOverAllocation === null) ? "NA" : 
                         (backendData?.allowOverAllocation === true || backendData?.allowOverAllocation === "YES") ? "YES" : "NO",
    overAllocationLimit: (backendData?.overAllocationLimit === undefined || backendData?.overAllocationLimit === null) ? "NA" : 
                         backendData?.overAllocationLimit?.toString() || "50",
    allowUnderAllocation: (backendData?.allowUnderAllocation === undefined || backendData?.allowUnderAllocation === null) ? "NO" : 
                         (backendData?.allowUnderAllocation === true || backendData?.allowUnderAllocation === "YES") ? "YES" : "NO",
    underAllocationLimit: (backendData?.allowUnderAllocationLimit === undefined || backendData?.allowUnderAllocationLimit === null) ? "10" : 
                         backendData?.allowUnderAllocationLimit?.toString() || "10",
    holdTimeBtnCreatedAndSWCMO: (backendData?.holdTimeBtnCreatedAndSWCMO === undefined || backendData?.holdTimeBtnCreatedAndSWCMO === null) ? "NA" : 
                                backendData?.holdTimeBtnCreatedAndSWCMO?.toString() || "12",
    allowLotAllocationBy: backendData?.allowLotAllocationBy === "all" ? "Both Sponsor and CMO (CMO only if Sponsor has not allocated batch before PO is shared)" : backendData?.allowLotAllocationBy || "Sponsor",
    sponsorTriggerStart: (backendData?.sponsorTriggerStart === undefined || backendData?.sponsorTriggerStart === null) ? "NA" :
      backendData?.sponsorTriggerStart === "poStatus" ? "PO Status" :
        backendData?.sponsorTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    sponsorTriggerEnd: (backendData?.sponsorTriggerEnd === undefined || backendData?.sponsorTriggerEnd === null) ? "NA" :
      backendData?.sponsorTriggerEnd === "poStatus" ? "PO Status" :
        backendData?.sponsorTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    cmoTriggerStart: (backendData?.cmoTriggerStart === undefined || backendData?.cmoTriggerStart === null) ? "NA" :
      backendData?.cmoTriggerStart === "poStatus" ? "PO Status" :
        backendData?.cmoTriggerStart === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
          "No. of days before expected delivery date",
    cmoTriggerEnd: (backendData?.cmoTriggerEnd === undefined || backendData?.cmoTriggerEnd === null) ? "NA" :
        backendData?.cmoTriggerEnd === "poStatus" ? "PO Status" :
        backendData?.cmoTriggerEnd === "scheduleMfgStartDateBefore" ? "No. of days before scheduled manufacturing start date" :
        "No. of days before expected delivery date",
    sponsorTriggerStartValue: backendData?.sponsorTriggerStart === "poStatus" ? 
      (poStatuses?.find((status) => status.status_id === backendData?.sponsorTriggerStartValue)?.status_name || backendData?.sponsorTriggerStartValue) : 
      backendData?.sponsorTriggerStartValue,
    sponsorTriggerEndValue: backendData?.sponsorTriggerEnd === "poStatus" ? 
      (poStatuses?.find((status) => status.status_id === backendData?.sponsorTriggerEndValue)?.status_name || backendData?.sponsorTriggerEndValue) : 
      backendData?.sponsorTriggerEndValue,
    cmoTriggerStartValue: backendData?.cmoTriggerStart === "poStatus" ? 
      (poStatuses?.find((status) => status.status_id === backendData?.cmoTriggerStartValue)?.status_name || backendData?.cmoTriggerStartValue) : 
      backendData?.cmoTriggerStartValue,
    cmoTriggerEndValue: backendData?.cmoTriggerEnd === "poStatus" ? 
      (poStatuses?.find((status) => status.status_id === backendData?.cmoTriggerEndValue)?.status_name || backendData?.cmoTriggerEndValue) : 
      backendData?.cmoTriggerEndValue,
  };
  
  return result;
}

// Add normalization for PO configuration
function normalizeOtherConfig(config) {
  if (!config) return {};
  return {
    mroDateFieldCalculation: config?.mroDateFieldCalculation === "" || config?.mroDateFieldCalculation == null
      ? null
      : Number(config?.mroDateFieldCalculation),
    cmoReadReceipts: !!config?.cmoReadReceipts,
    dispatchDuration: config?.dispatchDuration === "" || config?.dispatchDuration == null
      ? null
      : Number(config?.dispatchDuration),
    poAcknowledgementAvailable: !!config?.poAcknowledgementAvailable,
  };
}

// Update the normalization function
function normalizeStatusData(statusData) {
  if (!statusData) return {};

  const normalizeArray = arr =>
    (arr || []).map(status => {
      // If status has value but no activated, use value for activated
      if (status.value !== undefined && status.activated === undefined) {
        return {
          ...status,
          activated: status.value
        };
      }
      // If status has activated but no value, use activated for value
      if (status.activated !== undefined && status.value === undefined) {
        return {
          ...status,
          value: status.activated
        };
      }
      // If both exist, ensure they match
      if (status.activated !== undefined && status.value !== undefined) {
        return {
          ...status,
          value: status.activated,
          activated: status.activated
        };
      }
      // Default case
      return {
        ...status,
        value: false,
        activated: false
      };
    });

  return {
    poStatuses: normalizeArray(statusData.poStatuses)
  };
}

// Update the isSectionChanged function for fgStatuses
const isSectionChanged = (current, original, sectionKey, formDataObj, originalFormDataObj) => {
  let changed;
  if (sectionKey === 'fgStatuses') {
    const normCurrent = normalizeStatusData(current);
    const normOriginal = normalizeStatusData(original);

    function getStatusMap(arr) {
      const map = {};
      (arr || []).forEach(s => {
        map[s.status_id] = !!s.activated;
      });
      return map;
    }

    function compareStatusMaps(mapA, mapB) {
      // Only compare keys that exist in the current master list (mapA)
      for (const key of Object.keys(mapA)) {
        if ((mapA[key] || false) !== (mapB[key] || false)) {
          return false;
        }
      }
      return true;
    }

    const poStatusCurrent = getStatusMap(normCurrent.poStatuses);
    const poStatusOriginal = getStatusMap(normOriginal.poStatuses);
    const poStatusChanged = !compareStatusMaps(poStatusCurrent, poStatusOriginal);

    changed = poStatusChanged;
    return changed;
  }
  if (sectionKey === 'documentDeliverables') {
    const normCurrent = normalizeDocumentDeliverables(current);
    const normOriginal = normalizeDocumentDeliverables(original);
    changed = !isEqual(normCurrent, normOriginal);
    return changed;
  }
  if (sectionKey === 'allocationConfiguration') {
    // For allocation configuration, we need to handle the case where the data might be coming from API
    // with different formats (boolean vs string values)
    if (!current || !original) {
      return true; // If either is missing, consider it changed
    }
    
    // Convert both to a common format for comparison - use the same defaults as normalizeAllocationConfig
    const normCurrent = {
      showExpiredLots: current.showExpiredLots === true || current.showExpiredLots === "YES" ? "YES" : "NO",
      defaultAllocationReq: current.defaultAllocationReq === true || current.defaultAllocationReq === "YES" ? "YES" : "NO",
      qtyVarianceAcceptanceLimit: current.qtyVarianceAcceptanceLimit?.toString() || "10",
      allowMultipleLotAllocation: current.allowMultipleLotAllocation === true || current.allowMultipleLotAllocation === "YES" ? "YES" : "NO",
      allowOverAllocation: current.allowOverAllocation === true || current.allowOverAllocation === "YES" ? "YES" : 
                          current.allowOverAllocation === false || current.allowOverAllocation === "NO" ? "NO" : "NA",
      overAllocationLimit: current.overAllocationLimit === "NA" ? "NA" : (current.overAllocationLimit?.toString() || "50"),
      allowUnderAllocation: current.allowUnderAllocation === true || current.allowUnderAllocation === "YES" ? "YES" : "NO",
      underAllocationLimit: current.underAllocationLimit === "NA" ? "NA" : (current.underAllocationLimit?.toString() || "10"),
      holdTimeBtnCreatedAndSWCMO: current.holdTimeBtnCreatedAndSWCMO === "NA" ? "NA" : (current.holdTimeBtnCreatedAndSWCMO?.toString() || "12"),
      allowLotAllocationBy: current.allowLotAllocationBy || "Sponsor",
      sponsorTriggerStart: current.sponsorTriggerStart === "NA" ? "NA" : (current.sponsorTriggerStart || "PO Status"),
      sponsorTriggerEnd: current.sponsorTriggerEnd === "NA" ? "NA" : (current.sponsorTriggerEnd || "No. of days before expected delivery date"),
      cmoTriggerStart: current.cmoTriggerStart === "NA" ? "NA" : (current.cmoTriggerStart || "PO Status"),
      cmoTriggerEnd: current.cmoTriggerEnd === "NA" ? "NA" : (current.cmoTriggerEnd || "No. of days before expected delivery date"),
      sponsorTriggerStartValue: current.sponsorTriggerStartValue === "NA" ? "NA" : (current.sponsorTriggerStartValue?.toString() || ""),
      sponsorTriggerEndValue: current.sponsorTriggerEndValue === "NA" ? "NA" : (current.sponsorTriggerEndValue?.toString() || ""),
      cmoTriggerStartValue: current.cmoTriggerStartValue === "NA" ? "NA" : (current.cmoTriggerStartValue?.toString() || ""),
      cmoTriggerEndValue: current.cmoTriggerEndValue === "NA" ? "NA" : (current.cmoTriggerEndValue?.toString() || ""),
    };
    
    const normOriginal = {
      showExpiredLots: original.showExpiredLots === true || original.showExpiredLots === "YES" ? "YES" : "NO",
      defaultAllocationReq: original.defaultAllocationReq === true || original.defaultAllocationReq === "YES" ? "YES" : "NO",
      qtyVarianceAcceptanceLimit: original.qtyVarianceAcceptanceLimit?.toString() || "10",
      allowMultipleLotAllocation: original.allowMultipleLotAllocation === true || original.allowMultipleLotAllocation === "YES" ? "YES" : "NO",
      allowOverAllocation: original.allowOverAllocation === true || original.allowOverAllocation === "YES" ? "YES" : 
                          original.allowOverAllocation === false || original.allowOverAllocation === "NO" ? "NO" : "NA",
      overAllocationLimit: original.overAllocationLimit === "NA" ? "NA" : (original.overAllocationLimit?.toString() || "50"),
      allowUnderAllocation: original.allowUnderAllocation === true || original.allowUnderAllocation === "YES" ? "YES" : "NO",
      underAllocationLimit: original.underAllocationLimit === "NA" ? "NA" : (original.underAllocationLimit?.toString() || "10"),
      holdTimeBtnCreatedAndSWCMO: original.holdTimeBtnCreatedAndSWCMO === "NA" ? "NA" : (original.holdTimeBtnCreatedAndSWCMO?.toString() || "12"),
      allowLotAllocationBy: original.allowLotAllocationBy || "Sponsor",
      sponsorTriggerStart: original.sponsorTriggerStart === "NA" ? "NA" : (original.sponsorTriggerStart || "PO Status"),
      sponsorTriggerEnd: original.sponsorTriggerEnd === "NA" ? "NA" : (original.sponsorTriggerEnd || "No. of days before expected delivery date"),
      cmoTriggerStart: original.cmoTriggerStart === "NA" ? "NA" : (original.cmoTriggerStart || "PO Status"),
      cmoTriggerEnd: original.cmoTriggerEnd === "NA" ? "NA" : (original.cmoTriggerEnd || "No. of days before expected delivery date"),
      sponsorTriggerStartValue: original.sponsorTriggerStartValue === "NA" ? "NA" : (original.sponsorTriggerStartValue?.toString() || ""),
      sponsorTriggerEndValue: original.sponsorTriggerEndValue === "NA" ? "NA" : (original.sponsorTriggerEndValue?.toString() || ""),
      cmoTriggerStartValue: original.cmoTriggerStartValue === "NA" ? "NA" : (original.cmoTriggerStartValue?.toString() || ""),
      cmoTriggerEndValue: original.cmoTriggerEndValue === "NA" ? "NA" : (original.cmoTriggerEndValue?.toString() || ""),
    };
    
    // Compare the normalized versions
    const result = !isEqual(normCurrent, normOriginal);
    return result;
  }
  if (sectionKey === 'otherConfiguration') {
    const normCurrent = normalizeOtherConfig(current);
    const normOriginal = normalizeOtherConfig(original);
    changed = !isEqual(normCurrent, normOriginal);
    return changed;
  }
  if (sectionKey === 'processFlowConfiguration') {
    const normCurrent = normalizeProcessFlow(current);
    const normOriginal = normalizeProcessFlow(original);
    changed = !isEqual(normCurrent, normOriginal);
    return changed;
  }
  if (sectionKey === 'productDetails') {
    const [normCurrent, normOriginal] = normalizeProductDetails(current, original);
 
    changed = !isEqual(normCurrent, normOriginal);
    
    return changed;
  }
  changed = !isEqual(current, original);
  return changed;
};

// Helper function to sort materials - sponsor materials first, CMO-only after
// IMPORTANT: Handles duplicate bo_material_code where only first occurrence is sponsor-linked
const sortMaterials = (materialDetails) => {
  if (!materialDetails?.productMaterialsList) return materialDetails;
  
  // Track which bo_material_codes have been seen to identify duplicates
  // First occurrence with materialTypeName = sponsor material (linked to both sponsor and CMO tables)
  // Subsequent occurrences with same bo_material_code = CMO-only materials (no link to sponsor table)
  const seenBoMaterialCodes = new Set();
  
  const materialsWithFlag = materialDetails.productMaterialsList.map(material => {
    const hasMaterialType = material.materialTypeName && material.materialTypeName.trim() !== "";
    const boCode = material.bo_material_code;
    
    // Check if this bo_material_code has been seen before
    const isFirstOccurrence = !seenBoMaterialCodes.has(boCode);
    
    // Mark material as sponsor-linked only if:
    // 1. It has a materialTypeName AND
    // 2. It's the first occurrence of this bo_material_code
    if (hasMaterialType && boCode && isFirstOccurrence) {
      seenBoMaterialCodes.add(boCode);
      return { ...material, _isSponsorLinked: true };
    } else {
      return { ...material, _isSponsorLinked: false };
    }
  });
  
  return {
    ...materialDetails,
    productMaterialsList: materialsWithFlag.sort((a, b) => {
      // Sponsor-linked materials come first, then CMO-only materials
      if (a._isSponsorLinked && !b._isSponsorLinked) return -1;
      if (!a._isSponsorLinked && b._isSponsorLinked) return 1;
      return 0;
    })
  };
};

const ViewProductEdit = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const {
    selectedProduct,
    configuredProductDetails,
    viewProductDetails,
    selectedProductFormData,
    isEdit,
    isNotPending
  } = location.state || {};
  const [poStatuses, setPoStatuses] = useState([]);
  const [error, setError] = useState(null);
  const [editSection, setEditSection] = useState(null);
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const [saving, setSaving] = useState({});
  const [updateModeData, setUpdateModeData] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [isStatusEditMode, setIsStatusEditMode] = useState(false);
  const [isProductDetailsEditMode, setIsProductDetailsEditMode] = useState(false);
  const [isDocumentDeliverablesEditMode, setIsDocumentDeliverablesEditMode] = useState(false);
  const [isAllocationConfigEditMode, setIsAllocationConfigEditMode] = useState(false);
  const [isOtherConfigEditMode, setIsOtherConfigEditMode] = useState(false);
  const [isProcessFlowEditMode, setIsProcessFlowEditMode] = useState(false);
  const [isShipmentConfigEditMode, setIsShipmentConfigEditMode] = useState(false);
  const [currentCollabrixProductID, setCurrentCollabrixProductID] = useState(
    formData?.productDetails?.collabrixProductID || selectedProduct?.collabrixProductID
  );
  const [allFgStatuses, setAllFgStatuses] = useState(null);
  const [isProductDetailsValid, setIsProductDetailsValid] = useState(true); // Default to true for view/edit mode
  const [isStatusSelectionValid, setIsStatusSelectionValid] = useState(true); // Default to true for view/edit mode
  const [isDocumentDeliverablesValid, setIsDocumentDeliverablesValid] = useState(true); // Default to true for view/edit mode
  const [isAllocationConfigValid, setIsAllocationConfigValid] = useState(true); // Default to true for view/edit mode
  const [isOtherConfigValid, setIsOtherConfigValid] = useState(true); // Default to true for view/edit mode
  const [isProcessFlowValid, setIsProcessFlowValid] = useState(true); // Default to true for view/edit mode
  const [isShipmentConfigValid, setIsShipmentConfigValid] = useState(true); // Default to true for view/edit mode
  const [validationErrors, setValidationErrors] = useState({});
  const [productDetailsErrors, setProductDetailsErrors] = useState([]); // Store validation errors from ProductDetails
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [productDetailsKey, setProductDetailsKey] = useState(0); // Key to force ProductDetails remount
  const [deletedMaterials, setDeletedMaterials] = useState([]); // Track deleted materials and materials with changed codes (both have is_material_active: 1)
  
  // Ref to store latest material details from ProductDetails component
  // This ensures we use fresh data instead of potentially stale formData
  const latestMaterialDetailsRef = useRef(null);
  // Store the original CMO value for CMO change detection
  const originalCMORef = useRef(null);
  // Store original material codes from first API load to track material code changes across remounts
  const originalMaterialCodesRef = useRef([]);
  const documentDeliverablesRef = useRef(null);
  const allocationConfigurationRef = useRef(null);
  const productDetailsRef = useRef(null);
  const statusSelectionRef = useRef(null);
  const otherConfigurationsRef = useRef(null);
  const processFlowConfigurationRef = useRef(null);
  const shipmentConfigurationRef = useRef(null);


  // Change detection for all sections
  const isProductDetailsChanged = useMemo(() => {
    // Check regular productDetails changes
    const productDetailsResult = isSectionChanged(formData?.productDetails, originalFormData?.productDetails, 'productDetails', formData, originalFormData);
    
    // Also check sponsorCQAApprovalReq changes (stored in processFlowConfiguration but managed by ProductDetails component)
    const currentQACheck = formData?.processFlowConfiguration?.sponsorCQAApprovalReq;
    const originalQACheck = originalFormData?.processFlowConfiguration?.sponsorCQAApprovalReq;
    const qaCheckChanged = currentQACheck !== originalQACheck;
    
    // Check materialDetails changes (including additional CMO rows)
    const currentMaterials = formData?.materialDetails?.productMaterialsList || [];
    const originalMaterials = originalFormData?.materialDetails?.productMaterialsList || [];
    const materialDetailsChanged = currentMaterials.length !== originalMaterials.length || 
                                   !isEqual(formData?.materialDetails, originalFormData?.materialDetails);
    
    // Check if there are any deleted materials (is_material_active: 1)
    const hasDeletedMaterials = deletedMaterials && deletedMaterials.length > 0;
    
    const result = productDetailsResult || qaCheckChanged || materialDetailsChanged || hasDeletedMaterials;
    
    return result;
  }, [JSON.stringify(formData?.productDetails), JSON.stringify(originalFormData?.productDetails), formData?.processFlowConfiguration?.sponsorCQAApprovalReq, originalFormData?.processFlowConfiguration?.sponsorCQAApprovalReq, JSON.stringify(formData?.materialDetails), JSON.stringify(originalFormData?.materialDetails), deletedMaterials.length]);
  const isStatusSelectionChanged = useMemo(() => isSectionChanged(formData?.fgStatuses, originalFormData?.fgStatuses, 'fgStatuses', formData, originalFormData), [formData?.fgStatuses, originalFormData?.fgStatuses, formData, originalFormData]);
  const isDocumentDeliverablesChanged = useMemo(() => isSectionChanged(formData?.documentDeliverables, originalFormData?.documentDeliverables, 'documentDeliverables', formData, originalFormData), [formData?.documentDeliverables, originalFormData?.documentDeliverables, formData, originalFormData]);
  const isAllocationConfigChanged = useMemo(() => {
    const result = isSectionChanged(formData?.allocationConfiguration, originalFormData?.allocationConfiguration, 'allocationConfiguration', formData, originalFormData);
    return result;
  }, [formData?.allocationConfiguration, originalFormData?.allocationConfiguration, JSON.stringify(formData?.allocationConfiguration), JSON.stringify(originalFormData?.allocationConfiguration)]);
  const isOtherConfigChanged = useMemo(() => isSectionChanged(formData?.otherConfiguration, originalFormData?.otherConfiguration, 'otherConfiguration', formData, originalFormData), [formData?.otherConfiguration, originalFormData?.otherConfiguration, formData, originalFormData]);
  const isProcessFlowChanged = useMemo(() => isSectionChanged(formData?.processFlowConfiguration, originalFormData?.processFlowConfiguration, 'processFlowConfiguration', formData, originalFormData), [formData?.processFlowConfiguration, originalFormData?.processFlowConfiguration, formData, originalFormData]);
  const isShipmentConfigChanged = useMemo(() => isSectionChanged(formData?.shipmentConfiguration, originalFormData?.shipmentConfiguration, 'shipmentConfiguration', formData, originalFormData), [formData?.shipmentConfiguration, originalFormData?.shipmentConfiguration, formData, originalFormData]);



  useEffect(() => {
    let productDataToUse = null;
    if (configuredProductDetails) {
      productDataToUse = configuredProductDetails;
    } else if (isEdit && isNotPending && selectedProductFormData) {
      productDataToUse = selectedProductFormData;
    }
    if (!productDataToUse) {
      return;
    }
    setPageLoading(true);
    
    // Initialize deleted materials from API data (materials with is_material_active: 1)
    const deletedMaterialsFromAPI = productDataToUse?.materialDetails?.productMaterialsList
      ? productDataToUse.materialDetails.productMaterialsList.filter(material => material.is_material_active === 1)
      : [];
    if (deletedMaterialsFromAPI.length > 0) {
      setDeletedMaterials(deletedMaterialsFromAPI);
    }
    setPoStatuses(normalizeStatusData(productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {}));
    setFormData({
      productDetails: {
        productCode: productDataToUse?.productDetails?.productCode,
        productDescription: productDataToUse?.productDetails?.productDescription,
        brand: productDataToUse?.productDetails?.brand,
        status: productDataToUse?.productDetails?.status,
        aslNumber: productDataToUse?.productDetails?.aslNumber,
        supplyTypeID: productDataToUse?.productDetails?.supplyTypeID,
        productModality: productDataToUse?.productDetails?.drugTypeName,
        drugTypeID: productDataToUse?.productDetails?.drugTypeID,
        supplyTypeName: productDataToUse?.productDetails?.supplyTypeName,
        manufacturingFacilityType: productDataToUse?.productDetails?.facilityTypeName,
        facilityTypeID: productDataToUse?.productDetails?.facilityTypeID,
        cmoPlantId: productDataToUse?.productDetails?.vendorPlant,
        cmoProductCode: productDataToUse?.productDetails?.cmoProductCode,
        cmoProductDescription: productDataToUse?.productDetails?.cmoProductDescription,
        cmoProductDetailsList: productDataToUse?.productDetails?.cmoProductDetailsList || [],
        pendingConfiguration: productDataToUse?.productDetails?.pendingConfiguration,
        productTypeName: productDataToUse?.productDetails?.productTypeName,
        collabrixProductID: productDataToUse?.productDetails?.collabrixProductID,
        vendorMosymphonyID: productDataToUse?.productDetails?.vendorMosymphonyID,
        vendorPlant: productDataToUse?.productDetails?.vendorPlant,
        vendorName: productDataToUse?.productDetails?.vendorName,
        receivingStorageLocationIDsFromSponsorERP: productDataToUse?.productDetails?.receivingStorageLocationIDsFromSponsorERP,
        receivingPlantIDFromSponsorERP: productDataToUse?.productDetails?.receivingPlantIDFromSponsorERP,
        cmoUOM: productDataToUse?.productDetails?.cmoUOM,
        sponsorUOM: productDataToUse?.productDetails?.sponsorUOM,
      },
      materialDetails: (productDataToUse?.materialDetails && productDataToUse.materialDetails.productMaterialsList && productDataToUse.materialDetails.productMaterialsList.length > 0) ? sortMaterials({
        ...productDataToUse.materialDetails,
        productMaterialsList: productDataToUse.materialDetails.productMaterialsList.filter(m => m.is_material_active !== 1)
      }) : null,
      fgStatuses: normalizeStatusData(productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {}),
      documentDeliverables: (productDataToUse?.documentDetailsList || []).map(doc => ({
        ...doc,
        documentTypeID: doc.documentTypeID,
        fileFormatTypeID: doc.fileFormatTypeID,
        startStatus: doc.startOfUploadStatusText || doc.startOfUploadStatus || "Not Specified",
        endStatus: doc.endOfUploadStatusText || doc.endOfUploadStatus || "Not Specified",
        orgResponsibleID: doc.uploadOrgCollaboratorTypeID,
        mandatory: doc.documentMandatoryFlag,
        orgResponsibleName: doc.orgResponsibleName || "",
              })),
        allocationConfiguration: normalizeAllocationConfig(productDataToUse?.allocationConfiguration || {}, productDataToUse?.productFGStatuses?.fgStatusesJSON?.poStatuses || productDataToUse?.fgStatuses?.poStatuses || []),
      otherConfiguration: productDataToUse?.otherConfiguration || {
        mroDateFieldCalculation: productDataToUse?.otherConfiguration?.mroDateFieldCalculation ?? "",
        cmoReadReceipts: productDataToUse?.otherConfiguration?.cmoReadReceipts ?? false,
        dispatchDuration: productDataToUse?.otherConfiguration?.dispatchDuration ?? "",
        poAcknowledgementAvailable: productDataToUse?.otherConfiguration?.poAcknowledgementAvailable ?? true,
      },
      processFlowConfiguration: productDataToUse?.processFlowConfiguration,
      // shipmentConfiguration: productDataToUse?.shipmentConfiguration || { dispatchDuration: productDataToUse?.shipmentConfiguration?.dispatchDuration },
    });
    setOriginalFormData({
      productDetails: { 
        ...productDataToUse?.productDetails,
        cmoProductDetailsList: productDataToUse?.productDetails?.cmoProductDetailsList || []
      },
      materialDetails: (productDataToUse?.materialDetails && productDataToUse.materialDetails.productMaterialsList && productDataToUse.materialDetails.productMaterialsList.length > 0) ? sortMaterials({
        ...productDataToUse.materialDetails,
        productMaterialsList: productDataToUse.materialDetails.productMaterialsList.filter(m => m.is_material_active !== 1)
      }) : null,
              fgStatuses: normalizeStatusData(productDataToUse?.productFGStatuses?.fgStatusesJSON || productDataToUse?.fgStatuses || {}),
        allocationConfiguration: normalizeAllocationConfig(productDataToUse?.allocationConfiguration || {}, productDataToUse?.productFGStatuses?.fgStatusesJSON?.poStatuses || productDataToUse?.fgStatuses?.poStatuses || []),
      documentDeliverables: (productDataToUse?.documentDetailsList || []).map(doc => ({
        ...doc,
        documentTypeID: doc.documentTypeID,
        fileFormatTypeID: doc.fileFormatTypeID,
        startStatus: doc.startOfUploadStatusText || doc.startOfUploadStatus || "Not Specified",
        endStatus: doc.endOfUploadStatusText || doc.endOfUploadStatus || "Not Specified",
        orgResponsibleID: doc.uploadOrgCollaboratorTypeID,
        mandatory: doc.documentMandatoryFlag,
        orgResponsibleName: doc.orgResponsibleName || "",
      })),
      otherConfiguration: { ...productDataToUse?.otherConfiguration },
      processFlowConfiguration: { ...productDataToUse?.processFlowConfiguration },
      shipmentConfiguration: { ...(productDataToUse?.shipmentConfiguration || {}) },
    });
         // Set the original CMO value for later comparison
     if (productDataToUse?.productDetails?.vendorMosymphonyID != null) {
       originalCMORef.current = productDataToUse.productDetails.vendorMosymphonyID;
     }
     
     // Initialize originalMaterialCodesRef only on first load (when empty)
     // This preserves the original values even when component remounts with API data
     if (originalMaterialCodesRef.current.length === 0) {
       const allMaterials = productDataToUse?.materialDetails?.productMaterialsList || [];
       originalMaterialCodesRef.current = allMaterials
         .filter(m => m.is_material_active !== 1) // Only track active materials
         .map(material => ({
           id: material._materialId || `${material.sponsorSupplyingMaterial ? 'sponsor' : 'cmo'}_${material.bo_material_code || ''}_${material.cmo_material_code || ''}`,
           bo_material_code: material.bo_material_code || "",
           cmo_material_code: material.cmo_material_code || "",
           original_material: { ...material } // Store the complete original material object
         }));
     }
     
     setPageLoading(false);
  }, [configuredProductDetails, selectedProductFormData, isEdit, isNotPending]);



  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);


  const fetchProductDetails = async (collabrixProductIDParam) => {
    const idToUse = collabrixProductIDParam || currentCollabrixProductID;
    try {
      const response = await axios.get(
        `${apiBaseUrl}/fetchProductDetails/${idToUse}`
      );
      if (response.data && response.data.status) {
                 const apiData = response.data.data;
         setUpdateModeData(apiData);
         
         const normalizedAllocationConfig = normalizeAllocationConfig(apiData.allocationConfiguration || {}, apiData.productFGStatuses?.fgStatusesJSON?.poStatuses || []);
         
         setFormData({
           productDetails: { ...apiData.productDetails },
           materialDetails: (apiData.materialDetails && apiData.materialDetails.productMaterialsList && apiData.materialDetails.productMaterialsList.length > 0) ? sortMaterials({
             ...apiData.materialDetails,
             productMaterialsList: apiData.materialDetails.productMaterialsList.filter(m => m.is_material_active !== 1)
           }) : null,
           fgStatuses: normalizeStatusData(apiData.productFGStatuses?.fgStatusesJSON || apiData.fgStatuses || {}),
           allocationConfiguration: normalizedAllocationConfig,
          documentDeliverables: (apiData.documentDetailsList || []).map(doc => ({
            ...doc,
            documentTypeID: doc.documentTypeID || "",
            fileFormatTypeID: doc.fileFormatTypeID || "",
            startStatus: doc.startOfUploadStatusText || getStatusNameFromAllStatuses(doc.startOfUploadStatus) || "Not Specified",
            endStatus: doc.endOfUploadStatusText || getStatusNameFromAllStatuses(doc.endOfUploadStatus) || "Not Specified",
            orgResponsibleID: doc.uploadOrgCollaboratorTypeID || "",
            mandatory: doc.documentMandatoryFlag || false,
            orgResponsibleName: doc.orgResponsibleName || "",
          })),
          otherConfiguration: apiData.otherConfiguration || {},
          processFlowConfiguration: apiData.processFlowConfiguration || {},
          // shipmentConfiguration: { dispatchDuration: apiData.shipmentConfiguration?.dispatchDuration } || apiData.shipmentConfiguration || {},
        });
                 const normalizedOriginalAllocationConfig = normalizeAllocationConfig(apiData.allocationConfiguration || {}, apiData.productFGStatuses?.fgStatusesJSON?.poStatuses || []);
         
         setOriginalFormData({
           productDetails: { ...apiData.productDetails },
           materialDetails: (apiData.materialDetails && apiData.materialDetails.productMaterialsList && apiData.materialDetails.productMaterialsList.length > 0) ? sortMaterials({
             ...apiData.materialDetails,
             productMaterialsList: apiData.materialDetails.productMaterialsList.filter(m => m.is_material_active !== 1)
           }) : null,
           fgStatuses: normalizeStatusData(apiData.productFGStatuses?.fgStatusesJSON || apiData.fgStatuses || {}),
           allocationConfiguration: normalizedOriginalAllocationConfig,
          documentDeliverables: (apiData.documentDetailsList || []).map(doc => ({
            documentTypeID: doc.documentTypeID || "",
            fileFormatTypeID: doc.fileFormatTypeID || "",
            startStatus: doc.startOfUploadStatusText || getStatusNameFromAllStatuses(doc.startOfUploadStatus) || "Not Specified",
            endStatus: doc.endOfUploadStatusText || getStatusNameFromAllStatuses(doc.endOfUploadStatus) || "Not Specified",
            orgResponsibleID: doc.uploadOrgCollaboratorTypeID || "",
            mandatory: doc.documentMandatoryFlag || false,
            orgResponsibleName: doc.orgResponsibleName || "",
          })),
          otherConfiguration: apiData.otherConfiguration || {},
          processFlowConfiguration: apiData.processFlowConfiguration || {},
          // shipmentConfiguration: { ...(apiData.shipmentConfiguration || {}) },
        });
                 if (apiData.productDetails?.collabrixProductID) {
           setCurrentCollabrixProductID(apiData.productDetails.collabrixProductID);
         }
         
         // Update deleted materials from fetched data
         const fetchedDeletedMaterials = apiData?.materialDetails?.productMaterialsList
           ? apiData.materialDetails.productMaterialsList.filter(material => material.is_material_active === 1)
           : [];
         if (fetchedDeletedMaterials.length > 0) {
           setDeletedMaterials(fetchedDeletedMaterials);
         }
      } else {
        setError("Failed to fetch product details.");
      }
    } catch (error) {
      setError("An error occurred while fetching product details.");
    }
  };

  // Function to fetch FG statuses for dropdown options in Document Deliverables
  const fetchFGStatuses = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/fetchFGStatuses/true`);
      if (response?.data?.data) {
        setAllFgStatuses(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching FG statuses:", error);
    }
  };

  // Helper function to get status ID from both PO and batch statuses
  const getStatusIdFromAllStatuses = (statusName) => {
    // Return 0 for empty or invalid status names
    if (!statusName || statusName === "Not Specified") {
      return 0;
    }

    // PO statuses: From formData.fgStatuses (Status Selection step)
    const poStatuses = formData?.fgStatuses?.poStatuses || [];
    const poStatus = poStatuses.find(s => s.status_name === statusName);
    if (poStatus) return poStatus.status_id;

    // Batch statuses: From allFgStatuses (fetchfgstatuses API)
    const batchStatuses = allFgStatuses?.batchStatuses || [];
    const batchStatus = batchStatuses.find(s => s.status_name === statusName);
    if (batchStatus) return batchStatus.status_id;

    return 0; // Default fallback
  };

  // Helper function to get status name from status ID (for displaying existing data)
  const getStatusNameFromAllStatuses = (statusId) => {
    // Convert statusId to number for comparison
    const id = Number(statusId);
    
    // PO statuses: From formData.fgStatuses (Status Selection step)
    const poStatuses = formData?.fgStatuses?.poStatuses || [];
    const poStatus = poStatuses.find(s => s.status_id === id);
    if (poStatus) return poStatus.status_name;

    // Batch statuses: From allFgStatuses (fetchfgstatuses API)
    const batchStatuses = allFgStatuses?.batchStatuses || [];
    const batchStatus = batchStatuses.find(s => s.status_id === id);
    if (batchStatus) return batchStatus.status_name;

    return statusId; // Return original if not found
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchFGStatuses(); // Fetch FG statuses first
      await fetchProductDetails(); // Then fetch product details
    };
    initializeData();
  }, []);

  const handleGoBack = () => history.goBack();
  const updateStepData = (step, data) => {
    // Special handling for materialDetails to extract deleted materials and materials with changed codes
    if (step === 'materialDetails' && data?.productMaterialsList) {
      // Store the latest material details in ref for immediate use (bypasses React state async updates)
      latestMaterialDetailsRef.current = {
        productMaterialsList: data.productMaterialsList
      };
      
      // Extract materials marked for deletion (is_material_active: 1)
      // This includes both deleted materials and old versions of materials with changed codes
      const inactiveMaterials = data.productMaterialsList.filter(m => m.is_material_active === 1);
      const activeMaterials = data.productMaterialsList.filter(m => m.is_material_active !== 1);
      
      // Update deleted materials state with all inactive materials
      // These will be included in the API payload but not shown in the UI
      setDeletedMaterials(inactiveMaterials);
      
      // Update formData with only active materials for display
      setFormData((prev) => ({
        ...prev,
        [step]: {
          ...data,
          productMaterialsList: activeMaterials
        }
      }));
    }
    // Special handling for allocation configuration to ensure proper format
    else if (step === 'allocationConfiguration') {
      // Make sure we normalize the data to ensure consistent format
      setFormData((prev) => {
        const updatedData = { ...prev, [step]: { ...data } };
        return updatedData;
      });
    } else {
      setFormData((prev) => {
        const updatedData = { ...prev, [step]: data };
        return updatedData;
      });
    }
  };

  const saveHandlers = {
    productDetails: async () => {
     
      if (!isProductDetailsChanged) return;
      
      // Don't update originalFormData here - that should only happen after the global "Update Changes" is successful
      // The individual section save is just for UI purposes to exit edit mode and validate changes
      
      setEditSection(null);
      setIsProductDetailsEditMode(false);
    },
    statusSelection: async () => {


      if (!isStatusSelectionChanged) {

        setEditSection(null);
        setIsStatusEditMode(false);
        return;
      }


      setEditSection(null);
      setIsStatusEditMode(false);
    },
    documentDeliverables: async () => {
      const isChanged = isSectionChanged(formData?.documentDeliverables, originalFormData?.documentDeliverables, 'documentDeliverables', formData, originalFormData); 
      if (!isDocumentDeliverablesValid) {
        if (documentDeliverablesRef.current) {
          documentDeliverablesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      if (!isChanged) return;
      setEditSection(null);
      setIsDocumentDeliverablesEditMode(false);
    },
    allocationConfiguration: async () => {
      if (!isAllocationConfigChanged) {
        return;
      }
      
      // Don't update originalFormData here - that should only happen after the global "Update Changes" is successful
      // The individual section save is just for UI purposes to exit edit mode
      
      setEditSection(null);
      setIsAllocationConfigEditMode(false);
    },
    otherConfiguration: async () => {
      if (!isOtherConfigChanged) return;
      setEditSection(null);
      setIsOtherConfigEditMode(false);
    },
    processFlowConfiguration: async () => {
      if (!isProcessFlowChanged) return;
      setEditSection(null);
      setIsProcessFlowEditMode(false);
    },
    // shipmentConfiguration: async () => {
    //   if (!isShipmentConfigChanged) return;
    //   setEditSection(null);
    //   setIsShipmentConfigEditMode(false);
    // },
  };
  const handleDiscard = (section) => {
    if (section === 'productDetails') {
      setFormData((prev) => ({
        ...prev,
        productDetails: JSON.parse(JSON.stringify(originalFormData?.productDetails)),
        materialDetails: JSON.parse(JSON.stringify(originalFormData?.materialDetails))
      }));
      setIsProductDetailsEditMode(false);
      setProductDetailsErrors([]); // Clear validation errors
      // Force ProductDetails component to remount with fresh data
      setProductDetailsKey(prev => prev + 1);
    } else if (section === 'statusSelection') {
      setFormData((prev) => ({
        ...prev,
        fgStatuses: JSON.parse(JSON.stringify(originalFormData?.fgStatuses))
      }));
      setIsStatusEditMode(false);
    } else if (section === 'documentDeliverables') {
      setFormData((prev) => ({
        ...prev,
        documentDeliverables: JSON.parse(JSON.stringify(originalFormData?.documentDeliverables))
      }));
      setIsDocumentDeliverablesEditMode(false);
      setTimeout(() => {
        if (documentDeliverablesRef.current) {
          documentDeliverablesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    } else if (section === 'otherConfiguration') {
      setFormData((prev) => ({
        ...prev,
        otherConfiguration: JSON.parse(JSON.stringify(originalFormData?.otherConfiguration))
      }));
      setIsOtherConfigEditMode(false);
    } else if (section === 'allocationConfiguration') {
      setFormData((prev) => ({
        ...prev,
        allocationConfiguration: JSON.parse(JSON.stringify(originalFormData?.allocationConfiguration))
      }));
      setIsAllocationConfigEditMode(false);
    } else if (section === 'processFlowConfiguration') {
      setFormData((prev) => ({
        ...prev,
        processFlowConfiguration: JSON.parse(JSON.stringify(originalFormData?.processFlowConfiguration))
      }));
      setIsProcessFlowEditMode(false);
    }
    //  else if (section === 'documentDeliverables') {
    //   setFormData((prev) => ({
    //     ...prev,
    //     documentDeliverables: JSON.parse(JSON.stringify(originalFormData?.documentDeliverables))
    //   }));
    // }
    setEditSection(null);
  };
  const sections = [
    {
      key: "productDetails",
      label: "Product Details",
      Component: ProductDetails,
      stepKey: "productDetails",
      additionalProps: {
        editActiveProduct: true,
        onValidationChange: setIsProductDetailsValid,
        onValidationError: setProductDetailsErrors,
        isProductDetailsEditMode: isProductDetailsEditMode,
        originalMaterialCodesRef: originalMaterialCodesRef,
        deletedMaterials: deletedMaterials, // Pass deleted materials to ProductDetails
        ref: productDetailsRef // Pass ref to ProductDetails for force sync
      },
    },
    {
      key: "statusSelection",
      label: "Status Selection",
      Component: StatusSelection,
      stepKey: "fgStatuses",
      additionalProps: {
        readOnly: editSection === null || editSection !== "statusSelection",
        isStatusEditMode: isStatusEditMode,
        onValidationChange: setIsStatusSelectionValid
      }
    },
    {
      key: "allocationConfiguration",
      label: "Allocation Configurations",
      Component: AllocationConfigurations,
      stepKey: "allocationConfiguration",
      additionalProps: {
        readOnly: editSection === null || editSection !== "allocationConfiguration",
        isAllocationConfigEditMode: isAllocationConfigEditMode,
        onValidationChange: setIsAllocationConfigValid
      }
    },
    {
      key: "otherConfiguration",
      label: "Other Configurations",
      Component: OtherConfigurations,
      stepKey: "otherConfiguration",
      additionalProps: {
        readOnly: editSection === null || editSection !== "otherConfiguration",
        isOtherConfigEditMode: isOtherConfigEditMode,
        onValidationChange: setIsOtherConfigValid,
        viewOnlyProductDetails: viewProductDetails
      }
    },
    {
      key: "processFlowConfiguration",
      label: "Process Flow Configuration",
      Component: ProcessFlowConfiguration,
      stepKey: "processFlowConfiguration",
      additionalProps: {
        readOnly: editSection === null || editSection !== "processFlowConfiguration",
        isProcessFlowConfigEditMode: isProcessFlowEditMode,
        onValidationChange: setIsProcessFlowValid,
        viewOnlyProductDetails: viewProductDetails
      }
    },
    {
      key: "documentDeliverables",
      label: "Document Deliverables",
      Component: DocumentDeliverables,
      stepKey: "documentDeliverables",
      additionalProps: {
        readOnly: editSection === null || editSection !== "documentDeliverables",
        isDocumentDeliverablesEditMode: isDocumentDeliverablesEditMode,
        fgStatuses: formData?.fgStatuses,
        allFgStatuses: allFgStatuses, // Pass the fetched FG statuses
        originalFormData: originalFormData,
        onValidationChange: setIsDocumentDeliverablesValid
      }
    },
  ];

  // Add this function to get validation error message
  const getValidationErrorMessage = (section) => {
    if (saving[section]) return "Saving changes...";

    const errors = [];
    if (!formData[section]) {
      errors.push("");
    } else {
      switch (section) {
        case 'productDetails':
          // Don't show generic message if there are specific field errors
          // The inline field errors are more helpful
          if (!isProductDetailsValid && (!productDetailsErrors || productDetailsErrors.length === 0)) {
            errors.push("Please fill in all required fields");
          }
          break;
        case 'statusSelection':
          // if (!isStatusSelectionChanged) errors.push("No changes made");
          if (!isStatusSelectionValid) errors.push("Please select at least one status");
          break;
        case 'documentDeliverables':
          // if (!isDocumentDeliverablesChanged) errors.push("No changes made");
          if (!isDocumentDeliverablesValid) errors.push("Please fill in all required fields and fix any validation errors");
          break;
        case 'allocationConfiguration':
          if (!isAllocationConfigValid) errors.push("Please fill in all required fields");
          break;
        case 'otherConfiguration':
          // if (!isPOConfigChanged) errors.push("No changes made");
          if (!isOtherConfigValid) errors.push("Please fill in all required fields");
          break;
        case 'processFlowConfiguration':
          // if (!isProcessFlowChanged) errors.push("No changes made");
          if (!isProcessFlowValid) errors.push("Please fill in all required fields");
          break;
        // case 'shipmentConfiguration':
        //   // if (!isShipmentConfigChanged) errors.push("No changes made");
        //   if (!isShipmentConfigValid) errors.push("Please fill in all required fields");
        //   break;
      }
    }
    return errors.join(", ");
  };

  // Add a global Update Changes button and handler after all sections
  const anySectionChanged = isProductDetailsChanged || isStatusSelectionChanged || isAllocationConfigChanged || isOtherConfigChanged || isProcessFlowChanged || isDocumentDeliverablesChanged;
  // || isShipmentConfigChanged


  const handleUpdateAllChanges = async () => {
    // Check all validation flags
    if (
      !isProductDetailsValid ||
      !isDocumentDeliverablesValid ||
      !isAllocationConfigValid ||
      !isOtherConfigValid ||
      !isProcessFlowValid
      // !isShipmentConfigValid
    ) {
      // Scroll to the first invalid section
      if (!isProductDetailsValid && productDetailsRef?.current) {
        productDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (!isDocumentDeliverablesValid && documentDeliverablesRef?.current) {
        documentDeliverablesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (!isAllocationConfigValid && allocationConfigurationRef?.current) {
        allocationConfigurationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (!isOtherConfigValid && otherConfigurationsRef?.current) {
        otherConfigurationsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (!isProcessFlowValid && processFlowConfigurationRef?.current) {
        processFlowConfigurationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (!isShipmentConfigValid && shipmentConfigurationRef?.current) {
        shipmentConfigurationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return; // Do not call the update API
    }
    setPageLoading(true);
    try {
      // Force ProductDetails to sync its latest material details immediately
      // This ensures newly added materials have their material_type_id synced before building payload
      if (productDetailsRef?.current?.forceSyncMaterialDetails) {
        const syncedMaterials = productDetailsRef.current.forceSyncMaterialDetails();
        // Update latestMaterialDetailsRef with the synced data
        latestMaterialDetailsRef.current = {
          productMaterialsList: syncedMaterials
        };
      }
      
      // Normalize fgStatuses before building the payload
      const normalizedFgStatuses = normalizeStatusData(formData?.fgStatuses);
      
      // Get ALL PO statuses for allocation configuration lookup (not just activated ones)
      const allPoStatuses = normalizedFgStatuses?.poStatuses || [];
      
      // Get active PO statuses for other uses
      const activePoStatuses = normalizedFgStatuses?.poStatuses?.filter(s => s.activated) || [];

      // Prepare materialDetails with deleted materials included
      let materialDetailsForPayload = null;
      
      // Use latest material details from ProductDetails (via ref) instead of formData to avoid stale state
      // If ref is null, fall back to formData (for cases where materials haven't been edited)
      const currentMaterials = latestMaterialDetailsRef.current?.productMaterialsList || 
                               formData?.materialDetails?.productMaterialsList || [];
      const hasActiveMaterials = currentMaterials.length > 0;
      const hasDeletedMaterials = deletedMaterials.length > 0;
      
      if (hasActiveMaterials || hasDeletedMaterials) {
        // Fix: Detect missing deleted materials by comparing current with original materials
        // This catches cases where ProductDetails component didn't track the code change
        const missingDeletedMaterials = [];
        const originalMaterials = originalFormData?.materialDetails?.productMaterialsList || [];
        const activeCmoProductCode = currentMaterials.find(m => m.cmo_product_code)?.cmo_product_code || 
                                     formData?.productDetails?.cmoProductDetailsList?.[0]?.cmoProductCode || "";
        
        originalMaterials.forEach((originalMaterial) => {
          // Find corresponding current material using most robust matching strategy:
          // 1. Prefer _materialId matching (most reliable, unique identifier)
          // 2. Fallback to bo_material_code matching if _materialId not available
          // NOTE: Don't include material_type_id in matching - users can change material type!
          const currentMaterial = currentMaterials.find(curr => {
            // For sponsor materials
            if (originalMaterial.sponsorSupplyingMaterial && curr.sponsorSupplyingMaterial) {
              // Strategy 1: Match by _materialId if both have it (most reliable)
              if (originalMaterial._materialId && curr._materialId) {
                return curr._materialId === originalMaterial._materialId;
              }
              // Strategy 2: Fallback to bo_material_code matching
              return curr.bo_material_code === originalMaterial.bo_material_code;
            }
            // For CMO-only materials, match by bo_material_code AND cmo_material_code
            // We need exact match to detect code changes
            if (!curr.sponsorSupplyingMaterial && !originalMaterial.sponsorSupplyingMaterial) {
              // Strategy 1: Match by _materialId if both have it
              if (originalMaterial._materialId && curr._materialId) {
                return curr._materialId === originalMaterial._materialId;
              }
              // Strategy 2: Fallback to code matching
              return curr.bo_material_code === originalMaterial.bo_material_code &&
                     curr.cmo_material_code === originalMaterial.cmo_material_code;
            }
            return false;
          });
          
          if (currentMaterial) {
            // Material still exists - check if cmo_material_code changed
            const cmoCodeChanged = originalMaterial.cmo_material_code !== currentMaterial.cmo_material_code;
            
            if (cmoCodeChanged) {
              // Check if this deleted version is already in deletedMaterials
              const alreadyDeleted = deletedMaterials.some(deleted => 
                deleted.bo_material_code === originalMaterial.bo_material_code &&
                deleted.cmo_material_code === originalMaterial.cmo_material_code &&
                deleted.sponsorSupplyingMaterial === originalMaterial.sponsorSupplyingMaterial
              );
              
              if (!alreadyDeleted) {
                // Add the original material as deleted (code change scenario)
                missingDeletedMaterials.push({
                  ...originalMaterial,
                  is_material_active: 1,
                  cmo_product_code: activeCmoProductCode
                });
              }
            }
          } else {
            // Material was deleted - check if already tracked
            const alreadyDeleted = deletedMaterials.some(deleted => 
              deleted.bo_material_code === originalMaterial.bo_material_code &&
              deleted.cmo_material_code === originalMaterial.cmo_material_code &&
              deleted.sponsorSupplyingMaterial === originalMaterial.sponsorSupplyingMaterial
            );
            
            if (!alreadyDeleted) {
              // Add the original material as deleted (deletion scenario)
              missingDeletedMaterials.push({
                ...originalMaterial,
                is_material_active: 1,
                cmo_product_code: activeCmoProductCode
              });
            }
          }
        });
        
        // Filter deletedMaterials to remove invalid entries and duplicates
        const validDeletedMaterials = deletedMaterials.filter(deleted => {
          // Filter 1: Remove deleted materials with empty cmo_material_code (invalid intermediate states)
          if (!deleted.cmo_material_code || deleted.cmo_material_code.trim() === "") {
            
            return false;
          }
          
          // Filter 2: Remove duplicates that exist in current materials
          const existsInCurrent = currentMaterials.some(curr => {
            return curr.bo_material_code === deleted.bo_material_code &&
                   curr.cmo_material_code === deleted.cmo_material_code;
          });
          
          if (existsInCurrent) {
            
            return false;
          }
          
          // Filter 3: Check if this deleted material exists in originalMaterials (only keep deleted materials that were in original API response)
          const existedInOriginal = originalMaterials.some(orig => {
            return orig.bo_material_code === deleted.bo_material_code &&
                   orig.cmo_material_code === deleted.cmo_material_code;
          });
          
          if (!existedInOriginal) {
            
            return false;
          }
          
          return true;
        });
        
        // Combine active materials, valid deleted materials, and missing deleted materials
        const allMaterialsForPayload = [...currentMaterials, ...validDeletedMaterials, ...missingDeletedMaterials];
        
        // Fix: Restore cmo_product_code for deleted materials
        // This ensures old materials retain their cmo_product_code when sent to backend
        const fixedMaterialsForPayload = allMaterialsForPayload.map(material => {
          // Only fix deleted materials (is_material_active: 1) that are missing cmo_product_code
          if (material.is_material_active === 1 && (!material.cmo_product_code || material.cmo_product_code === "")) {
            return {
              ...material,
              cmo_product_code: activeCmoProductCode
            };
          }
          return material;
        });
        
        // Deduplicate materials - for deleted materials, ignore sponsorSupplyingMaterial flag
        // to prevent duplicates with different flags for the same bo_material_code + cmo_material_code
        const uniqueMaterialsMap = new Map();
        
        fixedMaterialsForPayload.forEach(material => {
          // Ensure material_type_id exists (set to null if missing) BEFORE processing
          // This ensures the field is always present in the final payload
          if (!('material_type_id' in material)) {
            material.material_type_id = null;
          }
          
          // For deleted materials, create key without sponsorSupplyingMaterial to catch all duplicates
          // For active materials, keep the original logic
          let finalKey;
          if (material.is_material_active === 1) {
            // Deleted material - key by bo_material_code + cmo_material_code only
            const baseKey = [
              material.bo_material_code || '',
              material.cmo_material_code || ''
            ].filter(Boolean).join('|');
            finalKey = `${baseKey}_deleted`;
          } else {
            // Active material - include sponsorSupplyingMaterial flag
            const keyParts = [
              material.sponsorSupplyingMaterial ? 'sponsor' : 'cmo',
              material.bo_material_code || '',
              material.cmo_material_code || ''
            ].filter(Boolean);
            const baseKey = keyParts.join('|');
            finalKey = `${baseKey}_active`;
          }
          
          if (!uniqueMaterialsMap.has(finalKey)) {
            uniqueMaterialsMap.set(finalKey, material);
          } else {
            const existing = uniqueMaterialsMap.get(finalKey);
            const existingHasTypeId = existing.material_type_id !== undefined && existing.material_type_id !== null && existing.material_type_id !== '';
            const materialHasTypeId = material.material_type_id !== undefined && material.material_type_id !== null && material.material_type_id !== '';
            
            // For duplicates, prefer the one with material_type_id (more complete data)
            // Also prefer sponsor version over CMO-only version for deleted materials
            if (materialHasTypeId && !existingHasTypeId) {
              uniqueMaterialsMap.set(finalKey, material);
            } else if (material.is_material_active === 1 && materialHasTypeId === existingHasTypeId && material.sponsorSupplyingMaterial && !existing.sponsorSupplyingMaterial) {
              // Prefer sponsor version for deleted materials when both have same material_type_id status
              uniqueMaterialsMap.set(finalKey, material);
            }
          }
        });
        
        // Clean up materials - remove internal tracking fields
        const deduplicatedMaterials = Array.from(uniqueMaterialsMap.values())
          .map((material, idx) => {
            
            const { _trackingId, _materialId, id, _isNewMaterial, _isExistingMaterial, ...cleanMaterial } = material;
            
            // Set materialTypeName to null for payload (API requirement)
            cleanMaterial.materialTypeName = null;
            
            // Explicitly ensure material_type_id is present (even if null)
            // This prevents the field from being omitted in the API payload
            if (!('material_type_id' in cleanMaterial)) {
              cleanMaterial.material_type_id = null;
            }
            
            return cleanMaterial;
          });
        
        materialDetailsForPayload = {
          productMaterialsList: deduplicatedMaterials
        };
        
      
        
      } else {
        materialDetailsForPayload = null;
      }
      
      let payload = {
        productDetails: formData?.productDetails,
        // Include materialDetails with deleted materials
        materialDetails: materialDetailsForPayload,
        // Always include allocation configuration to ensure proper ID format is sent
        allocationConfiguration: denormalizeAllocationConfig(formData?.allocationConfiguration || {}, allPoStatuses),
        ...(isProductDetailsChanged && { updateProductDetailsFlag: true }),
        ...(isStatusSelectionChanged && {
          updateFGStatusesFlag: true,
          productFGStatuses: {
            fgStatusesJSON: {
              poStatuses: (normalizedFgStatuses?.poStatuses || [])
                .filter((s) => s.activated)
                .map(({ status_id, status_name, activated }) => ({ status_id, status_name, activated })),
            },
          },
        }),
        ...(isDocumentDeliverablesChanged && {
          updateDocumentDeliverablesFlag: true,
          documentDetailsList: (formData?.documentDeliverables || []).map(doc => ({
            documentTypeID: doc.documentTypeID,
            fileFormatTypeID: doc.fileFormatTypeID,
            startOfUploadStatus: doc.startStatus === "Not Specified"
              ? "Not Specified"
              : String(getStatusIdFromAllStatuses(doc.startStatus)),
            endOfUploadStatus: doc.endStatus === "Not Specified"
              ? "Not Specified"
              : String(getStatusIdFromAllStatuses(doc.endStatus)),
            uploadOrgCollaboratorTypeID: doc.orgResponsibleID,
            documentMandatoryFlag: doc.mandatory,
            orgResponsibleName: doc.orgResponsibleName || "",
          })),
        }),
        ...(isAllocationConfigChanged && {
          updateAllocationConfigFlag: true,
        }),
        ...(isOtherConfigChanged && {
          updateOtherConfigFlag: true,
          otherConfiguration: {
            ...formData?.otherConfiguration,
          },
        }),
        ...(isProcessFlowChanged && {
          updateProcessFlowConfigFlag: true,
          processFlowConfiguration: formData?.processFlowConfiguration,
        }),
        // ...(isShipmentConfigChanged && {
        //   updateShipmentConfigFlag: true,
        //   shipmentConfiguration: formData?.shipmentConfiguration,
        // }),
      };
      // Detect CMO change
      const originalCMOValue = originalCMORef.current;
      const currentCMOValue = formData?.productDetails?.vendorMosymphonyID;
      const hasCMOChanged = originalCMOValue !== undefined && originalCMOValue !== currentCMOValue;
      // Check if any other fields have changed
      const hasOtherFieldsChanged = Object.keys(formData?.productDetails || {}).some(key => {
        const fieldsToIgnore = ['collabrixProductID', 'cmoPlantId'];
        if (fieldsToIgnore.includes(key)) return false;
        return originalFormData?.productDetails?.[key] !== formData?.productDetails?.[key];
      });
      // If CMO changed, add extra fields to payload
      if (hasCMOChanged) {
        payload = {
          ...payload,
          oldCollabrixProductID: originalFormData?.productDetails?.collabrixProductID,
          oldVendorMosymphonyID: originalCMOValue,
          updateCMOFlag: true,
        };
        // Always include productFGStatuses and documentDetailsList if available
        if (updateModeData?.productFGStatuses && Object.keys(updateModeData.productFGStatuses).length > 0) {
          payload.productFGStatuses = updateModeData.productFGStatuses;
        } else if (!payload.productFGStatuses) {
          payload.productFGStatuses = { fgStatusesJSON: {} };
        }
        if (updateModeData?.documentDetailsList && updateModeData.documentDetailsList.length > 0) {
          payload.documentDetailsList = updateModeData.documentDetailsList;
        } else if (!payload.documentDetailsList) {
          payload.documentDetailsList = [];
        }
        if (updateModeData?.allocationConfiguration && Object.keys(updateModeData.allocationConfiguration).length > 0) {
          // Use the already denormalized allocation configuration from payload, don't overwrite it
          // payload.allocationConfiguration = updateModeData.allocationConfiguration; 
        } else if (!payload.allocationConfiguration) {
          payload.allocationConfiguration = {};
        }
        if (updateModeData?.otherConfiguration && Object.keys(updateModeData.otherConfiguration).length > 0) {
          payload.otherConfiguration = updateModeData.otherConfiguration;
        } else if (!payload.otherConfiguration) {
          payload.otherConfiguration = {};
        }
        if (updateModeData?.processFlowConfiguration && Object.keys(updateModeData.processFlowConfiguration).length > 0) {
          payload.processFlowConfiguration = updateModeData.processFlowConfiguration;
        } else if (!payload.processFlowConfiguration) {
          payload.processFlowConfiguration = {};
        }
        // if (updateModeData?.shipmentConfiguration && Object.keys(updateModeData.shipmentConfiguration).length > 0) {
        //   payload.shipmentConfiguration = updateModeData.shipmentConfiguration;
        // } else if (!payload.shipmentConfiguration) {
        //   payload.shipmentConfiguration = {};
        // }
      }



      const response = await axios.post(`${apiBaseUrl}/updateProductDetails`, payload);
      if (response.data && response.data.collabrixProductID) {
        setCurrentCollabrixProductID(response.data.collabrixProductID);
        setFormData(prev => ({
          ...prev,
          productDetails: {
            ...prev.productDetails,
            collabrixProductID: response.data.collabrixProductID,
          }
        }));
        setOriginalFormData(prev => ({
          ...prev,
          productDetails: {
            ...prev.productDetails,
            collabrixProductID: response.data.collabrixProductID,
          }
        }));
        await fetchProductDetails(response.data.collabrixProductID);
      } else {
        await fetchProductDetails();
      }
      setShowSuccessPopup(true);
    } catch (e) {
      // handle error
    } finally {
      setPageLoading(false);
    }
  };

  const handlePopupOk = () => {
    setIsRedirecting(true);
    setShowSuccessPopup(false);
    // Redirect to dashboard after a short delay for loader effect
    setTimeout(() => {
      history.push("products");
    }, 500);
  };



  return (
    <>


      <Paper className={`${classes.root} view-product-wrapper`}>
        <Box className={classes.stepperHeader}>
          <Typography className={classes.heading} variant="h5">
            Update product details
          </Typography>
          <Typography className={classes.subheading}>
            Please update the product details and configurations in the fields below
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        <div className="view-product-wrapper-content">
          <div className={`stepper-container config-product-stepper-wrapper `}>
                         {selectedProduct && formData?.allocationConfiguration ? (
               sections.map(({ key, label, Component, stepKey, additionalProps = {} }, idx) => (
                <Paper elevation={0} className={`${classes.sectionPaper} ${key}`} key={key}
                  ref={
                    key === "productDetails" ? productDetailsRef :
                      key === "statusSelection" ? statusSelectionRef :
                        key === "documentDeliverables" ? documentDeliverablesRef :
                          key === "allocationConfiguration" ? allocationConfigurationRef :
                            key === "otherConfiguration" ? otherConfigurationsRef :
                              key === "processFlowConfiguration" ? processFlowConfigurationRef :
                                // key === "shipmentConfiguration" ? shipmentConfigurationRef :
                                undefined
                  }
                >
                  <Box className={classes.reviewSection}>
                    <Box className="section-actions" display="flex" justifyContent="space-between" alignItems="center">
                      {editSection !== key ? (
                        <IconButton
                          className={classes.iconButtonStyle}
                          onClick={() => {
                            setEditSection(key);
                            if (key === "productDetails") {
                              setIsProductDetailsEditMode(true);
                            } else if (key === "statusSelection") {
                              setIsStatusEditMode(true);
                            } else if (key === "documentDeliverables") {
                              setIsDocumentDeliverablesEditMode(true);
                            } else if (key === "allocationConfiguration") {
                              setIsAllocationConfigEditMode(true);
                            }
                            else if (key === "otherConfiguration") {
                              setIsOtherConfigEditMode(true);
                            } else if (key === "processFlowConfiguration") {
                              setIsProcessFlowEditMode(true);
                            }
                            // else if (key === "shipmentConfiguration") {
                            //   setIsShipmentConfigEditMode(true);
                            // }
                          }}
                          disabled={!!editSection}
                        >
                          <EditIcon />
                        </IconButton>
                      ) : (
                        <Box className={classes.actionButtons}>
                          <Button
                            variant='outlined'
                            className={classes.discardButton}
                            onClick={() => {
                              handleDiscard(key);
                              if (key === "productDetails") {
                                setIsProductDetailsEditMode(false);
                              } else if (key === "statusSelection") {
                                setIsStatusEditMode(false);
                              } else if (key === "documentDeliverables") {
                                setIsDocumentDeliverablesEditMode(false);
                              } else if (key === "allocationConfiguration") {
                                setIsAllocationConfigEditMode(false);
                              } else if (key === "otherConfiguration") {
                                setIsOtherConfigEditMode(false);
                              } else if (key === "processFlowConfiguration") {
                                setIsProcessFlowEditMode(false);
                              }
                              // else if (key === "shipmentConfiguration") {
                              //   setIsShipmentConfigEditMode(false);
                              // }
                            }}
                            disabled={saving[key]}
                          >
                            Discard Changes
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            disableElevation
                            disableRipple
                            disableFocusRipple
                            className={classes.saveButton}
                            onClick={() => {
                              saveHandlers[key]();
                              if (key === "statusSelection") {
                                setIsStatusEditMode(false);
                              } else if (key === "allocationConfiguration") {
                                setIsAllocationConfigEditMode(false);
                              } else if (key === "documentDeliverables") {
                                setIsDocumentDeliverablesEditMode(false);
                              }
                            }}
                            disabled={
                              (key === "productDetails" && (!isProductDetailsChanged || !isProductDetailsValid || saving.productDetails)) ||
                              (key === "statusSelection" && (!isStatusSelectionChanged || !isStatusSelectionValid || saving.statusSelection)) ||
                              (key === "documentDeliverables" && (!isDocumentDeliverablesChanged || !isDocumentDeliverablesValid || saving.documentDeliverables)) ||
                              (key === "allocationConfiguration" && (!isAllocationConfigChanged || !isAllocationConfigValid || saving.allocationConfiguration)) ||
                              (key === "otherConfiguration" && (!isOtherConfigChanged || !isOtherConfigValid || saving.otherConfiguration)) ||
                              (key === "processFlowConfiguration" && (!isProcessFlowChanged || !isProcessFlowValid || saving.processFlowConfiguration))
                              // (key === "shipmentConfiguration" && (!isShipmentConfigChanged || !isShipmentConfigValid || saving.shipmentConfiguration))
                            }
                          >
                            {saving[key] ? (
                              <>
                                Saving
                                <CircularProgress size={20} className={classes.buttonProgress} />
                              </>
                            ) : (
                              "Save Changes"
                            )}
                          </Button>

                        </Box>
                      )}
                    </Box>
                    <Component
                      key={key === "productDetails" ? productDetailsKey : key}
                      formData={formData}
                      classes={classes}
                      readOnly={editSection === null || editSection !== key}
                      updateStepData={updateStepData}
                      stepsLength={1}
                      {...additionalProps}
                    />
                    {editSection === key && getValidationErrorMessage(key) && (
                      <Typography
                        color="error"
                        variant="body2"
                        style={{
                          marginTop: '8px',
                          fontSize: '0.875rem',
                        }}
                      >
                        {getValidationErrorMessage(key)}
                      </Typography>
                    )}
                  </Box>
                  {idx < sections.length - 1 && <Divider />}
                </Paper>
              ))
            ) : (
              <Typography color="error" className={classes.noProductDataText}>
                No product data available. Please select a product from the list.
              </Typography>
            )}
            {/* Add the global Update Changes button here */}
            <Box display="flex" alignItems="center" justifyContent="center" mt={5} mb={5}>
              <Button
                className={classes.updateChangesButton}
                variant="contained"
                onClick={handleUpdateAllChanges}
                disabled={!anySectionChanged || pageLoading || !!editSection}
                endIcon={<ArrowForwardIosIcon style={{ fontSize: 18 }} />}
              >
                Update Changes
              </Button>
            </Box>
          </div>
        </div>
      </Paper>
      {pageLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.6)"
          zIndex={2000}
          style={{ zIndex: 2000 }}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      )}
      {/* Success Dialog */}
      <Dialog
        open={showSuccessPopup}
        onClose={handlePopupOk}
        fullWidth
        className="product-updated-success-popup"
        maxWidth="sm"
      >
        <StyledDialog
          title="Product Updated"
          subtitle="The product details have been updated successfully."
          icon={false} // false means show success (check), true means error (cross)
          handleClose={handlePopupOk}
          noCloseButton={true}
        />
      </Dialog>
      {isRedirecting && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.8)"
          zIndex={3000}
        >
          <CircularProgress size={60} color="primary" />
        </Box>
      )}
    </>
  );
};

export default ViewProductEdit;